import { useTranslate } from "@lobby/ocb-intl";
import * as Sentry from "@sentry/react";
import { type ServerErrorEventPayload, useErrorTranslate } from "../../lib";
import { Dialog } from "../dialog";
import type { IDialogProps } from "../dialog";
import { Icon } from "../icons";
import { getGMTTime, getLocalTime } from "./helpers";

interface IErrorModalProps extends IDialogProps {
  payload: ServerErrorEventPayload;
}

export function ErrorModal({ payload: { error } }: IErrorModalProps) {
  const { $t } = useTranslate();
  const { formatMessage } = useErrorTranslate();

  const localTime = getLocalTime();
  const GMTTime = getGMTTime();

  const title = $t({ defaultMessage: "error" });

  const details = error?.details ?? {};

  const filteredDetails = Object.entries(details).filter(
    ([key]) => key !== "playerId" && key !== "hallId"
  );

  function onClose() {
    window.dispatchEvent(
      new CustomEvent("modal:closed", {
        detail: {
          name: "error",
          meta: details,
        },
      })
    );
  }

  return (
    <Sentry.ErrorBoundary>
      <Dialog type="error" closable={false} onClose={onClose}>
        <Dialog.Title>
          <Icon name="errorSign" className="text-[2.5rem]" />
          <div>{title}</div>
        </Dialog.Title>
        <Dialog.Message>{formatMessage(error)}</Dialog.Message>
        <Dialog.Description>
          {filteredDetails.length > 0 && (
            <ul>
              {filteredDetails.map(([key, value]) => {
                if (!value) return null;

                return (
                  <li key={key} className="first-letter:capitalize">
                    {key}: {String(value)}
                  </li>
                );
              })}
            </ul>
          )}

          <div
            dir="ltr"
            style={{
              color: "var(--error-dialog-meta-text-color, #616161)",
            }}
            className="in-rtl:justify-end in-rtl:text-end text-12 mt-3 flex gap-5"
          >
            <div className="whitespace-nowrap">
              <div>
                <span>P:</span> {details.playerId ?? "N/A"}
              </div>
              <div>
                <span>H:</span> {details.hallId ?? "N/A"}
              </div>
            </div>
            <div className="grid grid-cols-[auto_auto] gap-x-1">
              <span>Local time:</span>
              <span>{localTime}</span>
              <span>UTC time:</span>
              <span>{GMTTime}</span>
            </div>
          </div>
        </Dialog.Description>
      </Dialog>
    </Sentry.ErrorBoundary>
  );
}
